import React, { useEffect, useState } from 'react';
import { ShieldCheckIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
export function SalesFooter() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    if (activeModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  return <>
      <footer className="bg-brown-dark text-beige py-12 px-4" data-id="element-165">
        <div className="max-w-4xl mx-auto text-center" data-id="element-166">
          <div className="flex justify-center items-center space-x-4 mb-8" data-id="element-167">
            <ShieldCheckIcon className="w-8 h-8 text-sage opacity-80" data-id="element-168" />
            <span className="text-lg font-medium opacity-90" data-id="element-169">
              Pago 100% Seguro y Encriptado
            </span>
          </div>

          <div className="text-sm opacity-60 space-y-4 max-w-2xl mx-auto" data-id="element-170">
            <p data-id="element-171">
              Descargo de responsabilidad: La información proporcionada en este
              sitio web y en el Protocolo Anti-Inflamación 72h tiene fines
              educativos e informativos únicamente. No pretende sustituir el
              consejo, diagnóstico o tratamiento médico profesional.
            </p>
            <p data-id="element-172">
              Siempre busque el consejo de su médico u otro proveedor de salud
              calificado con cualquier pregunta que pueda tener sobre una
              condición médica.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-beige/20 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-173">
            <button onClick={() => setActiveModal('privacy')} className="text-sm opacity-70 hover:opacity-100 underline underline-offset-4 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-sage rounded" data-id="element-174">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-sm opacity-70 hover:opacity-100 underline underline-offset-4 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-sage rounded" data-id="element-175">
              Términos y Condiciones
            </button>
          </div>

          <div className="mt-6 text-sm opacity-50" data-id="element-176">
            &copy; {new Date().getFullYear()} Protocolo Anti-Inflamación. Todos
            los derechos reservados.
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-177">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-178">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-brown-dark/70 backdrop-blur-sm" data-id="element-179" />

            {/* Modal content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-cream rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden z-10" data-id="element-180">
              {/* Header */}
              <div className="sticky top-0 bg-cream border-b border-beige px-6 py-5 flex items-center justify-between z-10" data-id="element-181">
                <h3 className="text-2xl font-bold text-brown-dark font-heading" data-id="element-182">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-beige transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta" aria-label="Cerrar" data-id="element-183">
                  <XIcon className="w-5 h-5 text-brown-body" data-id="element-184" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-8 overflow-y-auto max-h-[calc(80vh-80px)] text-brown-body space-y-6 text-sm leading-relaxed" data-id="element-185">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-186" /> : <TermsContent data-id="element-187" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-brown-body/60" data-id="element-188">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <section className="space-y-3" data-id="element-189">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-190">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-191">
          Recopilamos información personal que usted nos proporciona
          voluntariamente al realizar una compra, como su nombre, dirección de
          correo electrónico y datos de pago. No almacenamos información de
          tarjetas de crédito directamente; todos los pagos son procesados por
          plataformas de pago seguras de terceros.
        </p>
      </section>

      <section className="space-y-3" data-id="element-192">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-193">
          2. Uso de la Información
        </h4>
        <p data-id="element-194">Utilizamos su información personal para:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-195">
          <li data-id="element-196">
            Procesar y entregar su compra del Protocolo Anti-Inflamación 72h.
          </li>
          <li data-id="element-197">
            Enviarle el producto digital adquirido a su correo electrónico.
          </li>
          <li data-id="element-198">
            Comunicarnos con usted sobre su pedido o consultas de soporte.
          </li>
        </ul>
      </section>

      <section className="space-y-3" data-id="element-199">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-200">
          3. Protección de Datos
        </h4>
        <p data-id="element-201">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger su información personal contra acceso no autorizado,
          alteración, divulgación o destrucción. Utilizamos cifrado SSL para
          todas las transmisiones de datos.
        </p>
      </section>

      <section className="space-y-3" data-id="element-202">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-203">4. Cookies</h4>
        <p data-id="element-204">
          Nuestro sitio puede utilizar cookies para mejorar su experiencia de
          navegación y analizar el tráfico del sitio. Usted puede configurar su
          navegador para rechazar cookies, aunque esto podría afectar la
          funcionalidad del sitio.
        </p>
      </section>

      <section className="space-y-3" data-id="element-205">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-206">5. Terceros</h4>
        <p data-id="element-207">
          No vendemos, intercambiamos ni transferimos su información personal a
          terceros, excepto a proveedores de servicios de confianza que nos
          asisten en la operación de nuestro sitio web y la entrega de
          productos, siempre que dichas partes acuerden mantener esta
          información confidencial.
        </p>
      </section>

      <section className="space-y-3" data-id="element-208">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-209">6. Sus Derechos</h4>
        <p data-id="element-210">
          Usted tiene derecho a acceder, rectificar o eliminar su información
          personal en cualquier momento. Para ejercer estos derechos, puede
          comunicarse con nosotros a través de los canales disponibles en
          nuestro sitio.
        </p>
      </section>

      <section className="space-y-3" data-id="element-211">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-212">
          7. Cambios a esta Política
        </h4>
        <p data-id="element-213">
          Nos reservamos el derecho de actualizar esta política de privacidad en
          cualquier momento. Los cambios serán publicados en esta página con la
          fecha de la última actualización.
        </p>
      </section>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-brown-body/60" data-id="element-214">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <section className="space-y-3" data-id="element-215">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-216">
          1. Aceptación de los Términos
        </h4>
        <p data-id="element-217">
          Al acceder y utilizar este sitio web y al adquirir el Protocolo
          Anti-Inflamación 72h, usted acepta cumplir con estos términos y
          condiciones. Si no está de acuerdo con alguna parte de estos términos,
          le rogamos que no utilice nuestro sitio ni adquiera nuestros
          productos.
        </p>
      </section>

      <section className="space-y-3" data-id="element-218">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-219">
          2. Descripción del Producto
        </h4>
        <p data-id="element-220">
          El Protocolo Anti-Inflamación 72h es un producto digital en formato
          PDF que incluye una guía alimentaria, listas de compras, checklists y
          material educativo complementario. El producto se entrega de forma
          instantánea tras la confirmación del pago.
        </p>
      </section>

      <section className="space-y-3" data-id="element-221">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-222">
          3. Uso del Producto
        </h4>
        <p data-id="element-223">
          El contenido del protocolo es para uso personal y educativo
          únicamente. Queda prohibida la reproducción, distribución o reventa
          del material sin autorización expresa por escrito.
        </p>
      </section>

      <section className="space-y-3" data-id="element-224">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-225">
          4. Descargo de Responsabilidad Médica
        </h4>
        <p data-id="element-226">
          La información contenida en el Protocolo Anti-Inflamación 72h no
          constituye consejo médico profesional. Siempre consulte a su médico
          antes de iniciar cualquier programa alimentario o de bienestar. No nos
          hacemos responsables de los resultados individuales, ya que estos
          pueden variar de persona a persona.
        </p>
      </section>

      <section className="space-y-3" data-id="element-227">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-228">
          5. Política de Reembolso
        </h4>
        <p data-id="element-229">
          Debido a la naturaleza digital del producto y su entrega instantánea,
          todas las ventas son finales. Sin embargo, si experimenta problemas
          técnicos para acceder al producto, nos comprometemos a resolverlos de
          manera oportuna.
        </p>
      </section>

      <section className="space-y-3" data-id="element-230">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-231">
          6. Propiedad Intelectual
        </h4>
        <p data-id="element-232">
          Todo el contenido de este sitio web y del Protocolo Anti-Inflamación
          72h, incluyendo textos, gráficos, imágenes y diseño, está protegido
          por derechos de autor y es propiedad exclusiva de Protocolo
          Anti-Inflamación.
        </p>
      </section>

      <section className="space-y-3" data-id="element-233">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-234">
          7. Modificaciones
        </h4>
        <p data-id="element-235">
          Nos reservamos el derecho de modificar estos términos y condiciones en
          cualquier momento. Las modificaciones entrarán en vigor inmediatamente
          después de su publicación en el sitio web. El uso continuado del sitio
          después de dichos cambios constituye su aceptación de los nuevos
          términos.
        </p>
      </section>

      <section className="space-y-3" data-id="element-236">
        <h4 className="font-bold text-brown-dark text-base" data-id="element-237">
          8. Ley Aplicable
        </h4>
        <p data-id="element-238">
          Estos términos se regirán e interpretarán de acuerdo con las leyes
          aplicables, sin tener en cuenta sus disposiciones sobre conflictos de
          leyes.
        </p>
      </section>
    </>;
}